/**
 * `/editor` — draw a kitchen, and be told whether it is playable before a server
 * is ever started.
 *
 * This is the browser half of the same loop `npm run map -- validate` is the
 * terminal half of, and it is deliberately not a second implementation of
 * anything: it paints glyphs into the ASCII file format, runs `parseMap`,
 * `validateMap` and `mapReport` from `@overcooked/engine` — the very functions
 * the server boots on, reached as TypeScript source through the alias in
 * `vite.config.ts` — and hands back a `.txt` to drop into
 * `server/src/content/maps/`. A layout that is green here is a layout the server
 * will start on, because the same code said so.
 *
 * What it gets from the server is only what a browser cannot have: the recipe
 * book the dishes are walked through, and the presets as text to start from.
 * `GET /api/editor/content`, no clocks but the order clocks a `// schedule:` is
 * checked against.
 *
 * The checklist is the point of the page. The glyph-level mistakes are caught by
 * the parser and read as typos; the two that cost a round are a dish whose crate
 * is missing — found only by walking the recipe DAG backwards — and two seats
 * with no shared counter between them, which is a two-seat map that is not a
 * two-seat kitchen. Both are named as rules, live, while the kitchen is being
 * drawn.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CRATE_CONTENTS, MAP_LEGEND } from '@overcooked/engine'
import type { SerialisedState } from '@overcooked/shared'
import { EventFeed } from '../feed/feed'
import { Stage } from '../render/Stage'
import type { Catalog } from '../render/catalog'
import { EMPTY_CATALOG } from '../render/catalog'
import type { EditorContent } from './draft'
import {
  PALETTE,
  checkDraft,
  tileWords,
  downloadText,
  draftText,
  loadDraft,
  paint,
  parseDraft,
  resize,
  saveDraft,
  slug,
} from './draft'
import type { OracleCheck } from './oracleRow'
import { checkUntilAnswered, oracleHint, oracleRow } from './oracleRow'
import { previewState } from './preview'
import type { ScheduleRow } from './scheduleTable'
import { SECONDS, addEntry, clockTime, removeEntry, scheduleRows, writeSchedule } from './scheduleTable'
import { SWATCH_SIZE, drawGlyphSwatch, rackItemFor } from './swatch'
import { openRoomOn, saveMap } from './save'

/**
 * How long the draft has to sit still before the oracle is asked about it. The
 * check is a solve on a server shared with live rounds, so it waits for a
 * pause in the typing rather than following every keystroke.
 */
const ORACLE_CHECK_DELAY_MS = 800

/** What a brand new draft looks like: a room with a wall around it. */
const BLANK = ['// orders: fried_meat', 'W W W W W W W', ...Array(6).fill('W . . . . . W'), 'W W W W W W W'].join(
  '\n',
)

/**
 * The catalog the preview and the palette both draw with.
 *
 * The editor has the recipe book but not `/api/config`, so it draws crates from
 * their source ingredient alone — which is all a crate is. One object for both,
 * so a brush and the tile it paints can never disagree about an ingredient.
 */
const EDITOR_CATALOG: Catalog = EMPTY_CATALOG

const glyphTitle = (glyph: string): string => {
  if (/^[1-9]$/.test(glyph)) return `chef ${glyph} start`
  const type = MAP_LEGEND[glyph]
  if (!type) return `unknown tile "${glyph}"`
  return CRATE_CONTENTS[glyph] ? `${tileWords(type)} of ${CRATE_CONTENTS[glyph]}` : tileWords(type)
}

/**
 * The draft, drawn.
 *
 * A real `Stage` over a real `EventFeed`, exactly as the round page and
 * `/replay` mount it — the frame is a `KEYFRAME` of the unstarted round, which
 * is the same message a joining player receives. Re-pushed whenever the file
 * changes, so the picture follows the typing.
 *
 * No HUD and no self ring: there are no dockets, no score and no "you" in a
 * kitchen nobody is standing in yet, and a HUD full of zeroes beside the
 * checklist would be two panels saying nothing.
 */
function KitchenPreview({ state }: { state: SerialisedState }) {
  const feed = useMemo(() => new EventFeed(), [])
  useEffect(() => {
    feed.push({
      tick: state.tick,
      events: [{ type: 'KEYFRAME', tick: state.tick, wallClock: Date.now(), actor: 'editor', state }],
    })
  }, [feed, state])
  return (
    <Stage feed={feed} selfId={null} showHud={false} catalog={EDITOR_CATALOG} className="editor-stage" />
  )
}

/**
 * One brush's picture: the tile it paints, drawn by the renderer's own
 * `drawStation` (see `swatch.ts`), at the device's pixel ratio so it is crisp.
 */
function GlyphSwatch({ glyph, rackItem }: { glyph: string; rackItem: string | null }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    canvas.width = Math.round(SWATCH_SIZE * dpr)
    canvas.height = Math.round(SWATCH_SIZE * dpr)
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, SWATCH_SIZE, SWATCH_SIZE)
    drawGlyphSwatch(ctx, glyph, SWATCH_SIZE, EDITOR_CATALOG, { rackItem })
  }, [glyph, rackItem])
  return (
    <canvas
      ref={ref}
      className="editor-swatch"
      width={SWATCH_SIZE}
      height={SWATCH_SIZE}
      aria-hidden="true"
    />
  )
}

/**
 * The map's `// schedule:` as a table: when each ticket arrives.
 *
 * The rows are read off the file on every render and every edit writes the
 * lines back into it, so the textarea below stays the one source of truth — a
 * hand edit there re-derives this table, and an edit here changes only the
 * schedule lines. Whether the schedule is playable is the checklist's job: it
 * runs the same rail check the server boots on and names the tick that
 * overflows.
 */
function SchedulePanel({
  text,
  dishes,
  onChange,
}: {
  text: string
  dishes: Array<{ id: string; label: string }>
  onChange: (next: string) => void
}) {
  const rows = useMemo(() => scheduleRows(text), [text])
  const [seconds, setSeconds] = useState('0')
  const [dish, setDish] = useState(() => dishes[0]?.id ?? '')
  const chosen = dishes.some((d) => d.id === dish) ? dish : (dishes[0]?.id ?? '')
  const timeOk = SECONDS.test(seconds.trim())
  const write = (next: ScheduleRow[]) => onChange(writeSchedule(text, next))
  const labelOf = (id: string) => dishes.find((d) => d.id === id)?.label ?? id

  return (
    <section className="editor-schedule" data-testid="editor-schedule">
      <h3>Order schedule</h3>
      {rows.length === 0 ? (
        <p className="hint" role="status">
          No schedule. Tickets follow the orders line.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">Arrives</th>
              <th scope="col">Tickets</th>
              <th scope="col">
                <span className="visually-hidden">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, r) => (
              <tr key={`${r}:${row.line}`} data-row={r} data-seconds={row.seconds ?? ''}>
                <td className="editor-schedule-time">
                  {row.seconds === null ? <span title={row.line}>?</span> : clockTime(row.seconds)}
                </td>
                <td>
                  {row.seconds === null ? (
                    <code>{row.line}</code>
                  ) : (
                    <ul className="editor-schedule-dishes">
                      {row.dishes.map((id, d) => (
                        <li key={`${d}:${id}`}>
                          {labelOf(id)}
                          <button
                            type="button"
                            title={`Remove this ${labelOf(id)} ticket`}
                            aria-label={`Remove ${labelOf(id)} at ${clockTime(row.seconds as string)}`}
                            onClick={() => write(removeEntry(rows, r, d))}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className="editor-schedule-remove"
                    onClick={() => write(removeEntry(rows, r))}
                    aria-label={`Remove the row at ${row.seconds === null ? 'an unreadable time' : clockTime(row.seconds)}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form
        className="editor-schedule-add"
        onSubmit={(e) => {
          e.preventDefault()
          if (!timeOk || !chosen) return
          write(addEntry(rows, String(Number(seconds.trim())), chosen))
        }}
      >
        <label>
          At (s)
          <input
            name="seconds"
            value={seconds}
            inputMode="decimal"
            size={5}
            aria-invalid={!timeOk}
            onChange={(e) => setSeconds(e.target.value)}
          />
        </label>
        <label>
          Dish
          <select name="dish" value={chosen} onChange={(e) => setDish(e.target.value)}>
            {dishes.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={!timeOk || !chosen}>
          Add ticket
        </button>
      </form>
    </section>
  )
}

export default function EditorPage() {
  const [base, setBase] = useState<EditorContent | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  /**
   * What you were drawing last time, if anything.
   *
   * Read once, before the first paint, because it decides whether the preset
   * the server suggests is allowed to overwrite the file below: losing an
   * afternoon's kitchen to a reload is the one thing this page must not do.
   */
  const [restored] = useState(() => loadDraft())
  const [text, setText] = useState(() => restored?.text ?? BLANK)
  const [name, setName] = useState(() => restored?.name ?? 'untitled-kitchen')
  const [brush, setBrush] = useState('X')
  /** A save or a play in flight, so neither button can be pressed twice. */
  const [busy, setBusy] = useState<'save' | 'play' | null>(null)
  /** What the server said about the last save, in words rather than a colour. */
  const [notice, setNotice] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null)
  /** Held down: painting drags across cells the way a paint tool does. */
  const painting = useRef(false)

  // Every edit, straight to `localStorage`. Not debounced on purpose — see
  // `saveDraft`: a debounce is a window in which a reload eats the last thing
  // you typed.
  useEffect(() => {
    saveDraft({ text, name })
  }, [text, name])

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const res = await fetch('/api/editor/content')
        if (!res.ok) throw new Error(`the server returned ${res.status}`)
        const payload = (await res.json()) as EditorContent
        if (!live) return
        setBase(payload)
        // Open on the kitchen the study records on rather than on a blank grid:
        // a convention-breaking layout starts as an edit to a convention. Unless
        // there is a draft — then the recipe book is all that was wanted, and
        // the file on screen is yours.
        const preset = payload.maps.find((m) => m.name === payload.defaultMap) ?? payload.maps[0]
        if (preset && !restored) {
          setText(preset.text)
          setName(preset.name)
        }
      } catch (err) {
        if (live) setLoadError((err as Error).message)
      }
    })()
    return () => {
      live = false
    }
  }, [restored])

  const draft = useMemo(() => parseDraft(text), [text])
  const verdict = useMemo(() => (base ? checkDraft(text, slug(name), base) : null), [text, name, base])
  // Null while the file does not parse, which is most of the time a row is being
  // typed. The checklist says why; this just waits.
  const preview = useMemo(() => previewState(text, slug(name), base), [text, name, base])

  /**
   * The oracle row: once the checklist says the draft could be saved, ask the
   * server to play it (draft budget, never stored) and say what it found.
   * Nothing is sent while a rule above fails — the item-flow ✗ already says a
   * kitchen is impossible — and a newer edit cancels an older question. While
   * a room is in a round the server answers "queued", and the row says so and
   * asks again every few seconds until it is answered.
   */
  const [oracle, setOracle] = useState<OracleCheck>({ kind: 'idle' })
  const saveable = verdict?.ok === true
  const draftName = slug(name)
  useEffect(() => {
    if (!saveable) {
      setOracle({ kind: 'idle' })
      return
    }
    setOracle({ kind: 'pending' })
    const controller = new AbortController()
    const timer = setTimeout(() => {
      void checkUntilAnswered(draftName, text, setOracle, { signal: controller.signal })
    }, ORACLE_CHECK_DELAY_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [saveable, draftName, text])
  const oracleView = useMemo(
    () => oracleRow(oracle, base?.orderClock.roundLengthMs ?? 300_000),
    [oracle, base],
  )

  const apply = useCallback(
    (x: number, y: number) => {
      setText((current) => draftText(paint(parseDraft(current), x, y, brush)))
    },
    [brush],
  )

  const grow = useCallback((dx: number, dy: number) => {
    setText((current) => {
      const parsed = parseDraft(current)
      const width = parsed.rows[0]?.length ?? 0
      return draftText(resize(parsed, width + dx, parsed.rows.length + dy))
    })
  }, [])

  // `downloadText` rather than four lines here: an anchor has to be in the
  // document to be clickable and its object URL has to outlive the tick, and
  // neither is true of the version this replaced.
  const download = useCallback(() => {
    downloadText(text, `${slug(name)}.txt`)
  }, [text, name])

  /**
   * Save, and then play what was saved.
   *
   * One helper for both buttons, because "play" is "save and open a room on it"
   * — a kitchen that only exists in this tab is not one a server can seat two
   * people in. The refusal path is the interesting one: the server runs the same
   * `validateMap` the checklist beside this button is already running, so a save
   * that is refused means the two disagreed, and saying so is more useful than
   * saying "failed".
   */
  const store = useCallback(
    async (then: 'stay' | 'play') => {
      if (busy) return
      setBusy(then === 'play' ? 'play' : 'save')
      setNotice(null)
      const resolved = slug(name)
      const outcome = await saveMap(resolved, text, () =>
        window.prompt('Enter the admin password to save:'),
      )
      if (outcome.kind === 'cancelled') {
        setBusy(null)
        return
      }
      if (outcome.kind === 'needs-password') {
        setBusy(null)
        setNotice({ tone: 'bad', text: 'Wrong password. Try again.' })
        return
      }
      if (outcome.kind === 'refused') {
        setBusy(null)
        setNotice({ tone: 'bad', text: `${outcome.error}${outcome.refusedBecause ? ` ${outcome.refusedBecause}` : ''}` })
        return
      }
      if (outcome.kind === 'error') {
        setBusy(null)
        setNotice({ tone: 'bad', text: outcome.error })
        return
      }

      if (then === 'stay') {
        setBusy(null)
        setNotice({
          tone: 'ok',
          text: `Saved as "${outcome.name}". ${
            outcome.enabled
              ? 'Players can choose it now.'
              : 'It stays hidden until an admin shows it.'
          }`,
        })
        return
      }

      const room = await openRoomOn(outcome.name)
      if ('error' in room) {
        setBusy(null)
        setNotice({ tone: 'bad', text: `Saved as "${outcome.name}", but the game did not open. ${room.error}` })
        return
      }
      window.location.href = `/r/${room.code}`
    },
    [busy, name, text],
  )

  const width = draft.rows[0]?.length ?? 0
  const height = draft.rows.length
  // What the rack brush shows hanging in it: whatever a round would start it
  // with, by the recipe book's say-so. Empty until the book arrives.
  const rackItem = useMemo(() => rackItemFor(base, 'extinguisher_rack'), [base])

  return (
    <main className="page">
      <header className="bar">
        <span className="brand">
          Over<em>cooked</em>
        </span>
        <span className="bar-sub">Kitchen editor</span>
        <a className="bar-link" href="/">
          Back to the game
        </a>
      </header>

      {loadError && (
        <p className="hint">Couldn't load the recipes ({loadError}), so nothing can be checked.</p>
      )}

      <section className="editor-layout">
        <div className="editor-canvas">
          <div className="editor-tools">
            <label>
              Name
              <input value={name} onChange={(e) => setName(e.target.value)} spellCheck={false} />
            </label>
            {base && (
              <label>
                Start from
                <select
                  value=""
                  onChange={(e) => {
                    const preset = base.maps.find((m) => m.name === e.target.value)
                    if (!preset) return
                    setText(preset.text)
                    setName(preset.name)
                  }}
                >
                  <option value="">Choose a kitchen…</option>
                  {base.maps.map((m) => (
                    <option key={m.name} value={m.name}>
                      {m.name} · {m.seats === 1 ? '1 player' : `${m.seats} players`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <span className="editor-size">
              {width}×{height}
            </span>
            <span className="editor-steppers">
              <button onClick={() => grow(1, 0)} title="Add a column">+ Col</button>
              <button onClick={() => grow(-1, 0)} title="Remove a column">− Col</button>
              <button onClick={() => grow(0, 1)} title="Add a row">+ Row</button>
              <button onClick={() => grow(0, -1)} title="Remove a row">− Row</button>
            </span>
            <button onClick={() => setText(BLANK)} title="Start again from an empty kitchen">
              Clear
            </button>
            <button onClick={download} disabled={!verdict}>
              Download .txt
            </button>
            {/* Both disabled until the checklist is green, because the server
                runs the same validator and would refuse it anyway — and a
                button that always fails is a button that teaches nothing. */}
            <button onClick={() => void store('stay')} disabled={!verdict?.ok || busy !== null}>
              {busy === 'save' ? 'Saving…' : 'Save'}
            </button>
            <button onClick={() => void store('play')} disabled={!verdict?.ok || busy !== null}>
              {busy === 'play' ? 'Opening…' : 'Save and play'}
            </button>
          </div>

          {notice && (
            <p className="hint" data-tone={notice.tone} role="status">
              {notice.text}
            </p>
          )}

          <ul className="editor-palette">
            {PALETTE.map((entry) => (
              <li key={entry.glyph}>
                <button
                  type="button"
                  className={entry.glyph === brush ? 'chosen' : undefined}
                  aria-pressed={entry.glyph === brush}
                  onClick={() => setBrush(entry.glyph)}
                  title={`${entry.glyph} · ${entry.label}`}
                >
                  <GlyphSwatch glyph={entry.glyph} rackItem={rackItem} />
                  <span className="editor-glyph">{entry.glyph}</span>
                  <span className="editor-glyph-label" title={entry.label}>
                    {entry.label}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          {/* The kitchen. One button per cell, so a click is a paint and the
              keyboard can reach every tile — a grid of divs would be neither.

              Three ways in, because a <button> only makes that claim true if it
              is wired for all of them: `onClick` is what the *keyboard* raises
              (Enter and Space on a focused button dispatch click and never
              mousedown), `onPointerDown` is what makes a mouse press paint
              immediately and a tap paint on a tablet, and `onPointerEnter` is
              the drag. Mouse presses therefore paint twice, once on each of the
              first two, which is exactly nothing: `paint` writes one glyph into
              one cell and is idempotent, so the second call produces the same
              file as the first. */}
          <div
            className="editor-grid"
            style={{ gridTemplateColumns: `repeat(${Math.max(1, width)}, 28px)` }}
            onPointerLeave={() => {
              painting.current = false
            }}
            onPointerUp={() => {
              painting.current = false
            }}
            onPointerCancel={() => {
              painting.current = false
            }}
          >
            {draft.rows.flatMap((row, y) =>
              row.map((glyph, x) => (
                <button
                  key={`${x},${y}`}
                  className="editor-cell"
                  type="button"
                  data-glyph={glyph}
                  title={`(${x},${y}) ${glyphTitle(glyph)}`}
                  aria-label={`(${x},${y}) ${glyphTitle(glyph)}`}
                  onClick={() => apply(x, y)}
                  onPointerDown={() => {
                    painting.current = true
                    apply(x, y)
                  }}
                  onPointerEnter={() => {
                    if (painting.current) apply(x, y)
                  }}
                >
                  {glyph === '.' ? '·' : glyph}
                </button>
              )),
            )}
          </div>

          {/* What the letters above are. The grid is the file; this is the
              kitchen, drawn by the renderer a player looks at — so a pan placed
              by a `// container:` line either lands on the stove here or it does
              not land on it in a round either. */}
          <div className="editor-preview">
            <span className="editor-preview-label">Preview</span>
            {preview ? (
              // Keyed by the size of the room: the stage measures itself once
              // per map, so a kitchen that grew a column gets a fresh feed
              // rather than a canvas laid out for the old one.
              <KitchenPreview key={`${width}x${height}`} state={preview} />
            ) : (
              <p className="hint">
                {base
                  ? 'The kitchen appears here once the file is valid.'
                  : 'Loading recipes…'}
              </p>
            )}
          </div>

          {/* The file itself, because the directives are content and a grid of
              buttons cannot express "this kitchen starts with a pan at 6,2". */}
          <label className="editor-source">
            Kitchen file
            <textarea
              value={text}
              spellCheck={false}
              rows={Math.min(24, height + draft.directives.length + 2)}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
        </div>

        <aside className="editor-report">
          <h2
            data-testid="editor-verdict"
            data-ok={verdict === null ? undefined : verdict.ok ? 'true' : 'false'}
          >
            {verdict === null
              ? 'Loading recipes…'
              : verdict.ok
                ? 'This kitchen works'
                : 'This kitchen has problems'}
          </h2>

          {verdict && (
            <>
              <ul className="editor-checks">
                {verdict.checks.map((check) => (
                  <li
                    key={check.rule}
                    data-ok={check.ok ? 'yes' : 'no'}
                    data-advisory={check.advisory ? 'yes' : 'no'}
                    data-save-only={check.saveOnly ? 'yes' : undefined}
                  >
                    {/* A failed advisory rule is marked "!" rather than "✗": the
                        column has to distinguish "the server will not start" from
                        "this plays, and not as two chefs". */}
                    <span className="editor-check-mark">{check.ok ? '✓' : check.advisory ? '!' : '✗'}</span>
                    <span className="editor-check-rule">{check.rule}</span>
                    <span className="editor-check-detail">{check.detail}</span>
                  </li>
                ))}
                {/* Last, and advice whatever it says: a search that serves
                    nothing is evidence, not proof, so it never turns Save off. */}
                {oracleView && (
                  <li key="oracle" data-rule="oracle" data-ok={oracleView.state} data-advisory="yes">
                    <span className="editor-check-mark">{oracleView.mark}</span>
                    <span className="editor-check-rule">{oracleView.rule}</span>
                    <span className="editor-check-detail">{oracleView.detail}</span>
                  </li>
                )}
              </ul>

              {oracleHint(oracle) && (
                <p className="hint" data-testid="oracle-hint" data-tone="bad" role="status">
                  {oracleHint(oracle)}
                </p>
              )}

              {verdict.refusedBecause === null && verdict.saveRefusals.length > 0 && (
                <p className="editor-refusal" data-testid="editor-save-refusal">
                  <strong>The server would start on it, but it cannot be saved:</strong>{' '}
                  {verdict.saveRefusals.map((check) => check.detail).join('; ')}
                </p>
              )}

              {verdict.refusedBecause && (
                <p className="editor-refusal">
                  <strong>The server would refuse it:</strong> {verdict.refusedBecause}
                </p>
              )}

              {/* Said out loud, because otherwise the one "!" in a green column
                  reads as a bug in the page. */}
              {verdict.ok && verdict.advice.length > 0 && (
                <p className="editor-advice">
                  <strong>Suggestions:</strong> {verdict.advice.map((check) => check.detail).join('; ')}.
                </p>
              )}

              {/* No summary panel under the checklist: the seats, the shared
                  counters and the dish pool are every one of them a rule above,
                  and a page that says the same thing twice is a page somebody
                  reads once. */}
            </>
          )}

          {base && (
            <SchedulePanel
              text={text}
              dishes={base.recipes.dishes.map((d) => ({ id: d.id, label: d.label }))}
              onChange={setText}
            />
          )}

          <p className="hint">
            Save needs the admin password. New kitchens stay hidden until an admin shows them.
          </p>
        </aside>
      </section>
    </main>
  )
}
